import { Component } from 'react';
import { WarningCircle, ArrowClockwise } from '@phosphor-icons/react';

/**
 * ErrorBoundary - Catches JavaScript errors in child component tree
 * Displays a fallback UI instead of crashing the whole app
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error for debugging
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
    
    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    
    // Call optional onReset callback
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Use fallback render function if provided
      if (this.props.fallbackRender) {
        return this.props.fallbackRender({
          error: this.state.error,
          resetError: this.handleReset,
        });
      }
      
      // Default fallback UI
      return (
        <ErrorFallback
          error={this.state.error}
          resetError={this.handleReset}
          title={this.props.fallbackTitle}
          compact={this.props.compact}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI component
 */
export function ErrorFallback({ 
  error, 
  resetError, 
  title = 'Something went wrong',
  compact = false,
}) {
  if (compact) {
    return (
      <div 
        className="flex items-center gap-2 p-3 rounded-sm"
        style={{ 
          backgroundColor: 'rgba(193, 96, 92, 0.1)',
          border: '1px solid var(--color-status-error)',
        }}
      >
        <WarningCircle size={20} style={{ color: 'var(--color-status-error)' }} />
        <span className="text-sm flex-1" style={{ color: 'var(--color-status-error)' }}>
          {title}
        </span>
        {resetError && (
          <button
            onClick={resetError}
            className="p-1 rounded-sm hover:bg-black/10 transition-colors"
            title="Try again"
          >
            <ArrowClockwise size={16} style={{ color: 'var(--color-status-error)' }} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div 
      className="flex flex-col items-center justify-center p-8 rounded-lg text-center"
      style={{ 
        backgroundColor: 'var(--color-bg-surface)',
        border: '1px solid var(--color-border-subtle)',
      }}
    >
      <div 
        className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
        style={{ backgroundColor: 'rgba(193, 96, 92, 0.1)' }}
      >
        <WarningCircle size={24} style={{ color: 'var(--color-status-error)' }} />
      </div>
      
      <h3 
        className="text-lg font-medium mb-2"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {title}
      </h3>
      
      <p 
        className="text-sm mb-4 max-w-md"
        style={{ color: 'var(--color-text-secondary)' }}
      >
        An unexpected error occurred. Please try again or refresh the page.
      </p>
      
      {process.env.NODE_ENV === 'development' && error && (
        <details className="mb-4 text-left w-full max-w-md">
          <summary 
            className="text-xs cursor-pointer mb-2"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Error details
          </summary>
          <pre 
            className="text-xs p-3 rounded-sm overflow-auto max-h-32"
            style={{ 
              backgroundColor: 'var(--color-bg-raised)',
              color: 'var(--color-text-secondary)',
            }}
          >
            {error.toString()}
            {error.stack && `\n\n${error.stack}`}
          </pre>
        </details>
      )}
      
      <div className="flex gap-2">
        {resetError && (
          <button
            onClick={resetError}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowClockwise size={16} />
            Try Again
          </button>
        )}
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

/**
 * Page-level error boundary with full-page fallback
 */
export function PageErrorBoundary({ children }) {
  return (
    <ErrorBoundary
      fallbackRender={({ error, resetError }) => (
        <div 
          className="min-h-screen flex items-center justify-center p-4"
          style={{ backgroundColor: 'var(--color-bg-canvas)' }}
        >
          <ErrorFallback 
            error={error} 
            resetError={resetError}
            title="Page failed to load"
          />
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Section-level error boundary with compact fallback
 */
export function SectionErrorBoundary({ children, title = 'This section failed to load' }) {
  return (
    <ErrorBoundary compact fallbackTitle={title}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Card-level error boundary
 */
export function CardErrorBoundary({ children, title = 'Failed to load' }) {
  return (
    <ErrorBoundary
      fallbackRender={({ resetError }) => (
        <div 
          className="card p-4 flex flex-col items-center justify-center text-center min-h-[120px]"
        >
          <WarningCircle 
            size={24} 
            className="mb-2"
            style={{ color: 'var(--color-text-muted)' }} 
          />
          <p 
            className="text-sm mb-2"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            {title}
          </p>
          {resetError && (
            <button
              onClick={resetError}
              className="text-xs flex items-center gap-1"
              style={{ color: 'var(--color-brand-primary)' }}
            >
              <ArrowClockwise size={12} />
              Retry
            </button>
          )}
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

export default ErrorBoundary;
